import {
  usePostHubspotEvent,
  usePostHubspotProperty,
} from "@/features/analytics";
import { registerAnalyticsProperty } from "@/shared";
import {
  useGetCurrentUserQuery,
  useUpdateAutosaveCountMutation,
} from "@/shared/services";
import { trackContent } from "@editor/utils";
import { useCallback, useEffect, useRef } from "react";

const AUTOSAVE_COUNT_THRESHOLD = 10;

/**
 * Autosave engagement telemetry: the Mixpanel "autosave counter" property and
 * engagement level, the HubSpot "first time edit" event and `autosave_count`
 * property, and the persisted per-user autosave count. Nothing here touches
 * the save itself — `useAutosave` calls the returned `onSaved()` after a
 * successful page save, and the pending count is flushed on unmount.
 */
export const useAutosaveAnalytics = () => {
  const { data: me } = useGetCurrentUserQuery();
  const { mutate: updateAutosaveCount } = useUpdateAutosaveCountMutation();
  const { mutate: postHubspotEvent } = usePostHubspotEvent();
  const { mutate: postHubspotProperty } = usePostHubspotProperty();
  const totalAutosaveCount = useRef(me?.autosaveCount || 0);
  const currentAutosaveCount = useRef(0);
  // A save can complete after this hook unmounted (the editor route remounts
  // on template <-> version navigation while the debounced save is in
  // flight). The unmount flush below has already run by then, so such a late
  // save must flush its own count instead of waiting for a threshold nobody
  // will reach.
  const mounted = useRef(true);

  const handleAutosaveCount = useCallback(
    (forceCount?: boolean) => {
      // ======== FIRST TIME EDIT ========
      if (totalAutosaveCount.current === 1 && !forceCount) {
        postHubspotEvent({ event: "first time edit" });
        updateAutosaveCount(1);
      }

      // ======== AUTOSAVE COUNT ========
      const count = currentAutosaveCount.current;
      if (count && (count % AUTOSAVE_COUNT_THRESHOLD === 0 || forceCount)) {
        updateAutosaveCount(count);
        postHubspotProperty({
          autosave_count: totalAutosaveCount.current,
        });
        currentAutosaveCount.current = 0;
      }
    },
    // The HubSpot mutators are intentionally not deps: the unmount flush below
    // keys on this callback, and a new identity per render would flush the
    // count on every commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateAutosaveCount],
  );

  const onSaved = useCallback(() => {
    trackContent("success edit save");
    registerAnalyticsProperty({
      property: "autosave counter",
      value: totalAutosaveCount.current + 1,
    });
    totalAutosaveCount.current += 1;
    currentAutosaveCount.current += 1;
    handleAutosaveCount(!mounted.current);
  }, [handleAutosaveCount]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      handleAutosaveCount(true);
    };
  }, [handleAutosaveCount]);

  useEffect(() => {
    if (me?.autosaveCount) {
      const level = getEngagementLevel(me.autosaveCount || 0);
      if (level) trackContent("autosave level", { level });
    }
  }, [me?.autosaveCount]);

  return onSaved;
};

const getEngagementLevel = (editCount: number): string | null => {
  const levels = [
    { threshold: 10, label: "less than 10 edits" },
    { threshold: 50, label: "10-50 edits" },
    { threshold: 100, label: "50-100 edits" },
    { threshold: 500, label: "100-500 edits" },
    { threshold: Number.POSITIVE_INFINITY, label: "500+ edits" },
  ];

  const level = levels.find((level) => editCount < level.threshold);

  return level ? level.label : null;
};
